import { AdvancedPolicyEngine, PolicyConfigManager } from '../policies/policyEngine';
import type { PolicyRule } from '../policies/policyEngine';

/**
 * RBAC Configuration System
 * Centralized configuration for all RBAC policies and settings
 */

export interface RBACConfig {
  version: string;
  environment: 'development' | 'staging' | 'production';
  policies: {
    enabled: boolean;
    strictMode: boolean;
    defaultDeny: boolean;
    auditMode: boolean;
    customPolicies: PolicyRule[];
  };
  features: {
    timeBasedAccess: boolean;
    ipRestrictions: boolean;
    sessionTimeout: boolean;
    multiFactorAuth: boolean;
  };
  audit: {
    logAccess: boolean;
    logDenials: boolean;
    logPolicyChanges: boolean;
    retentionDays: number;
  };
  ui: {
    showPermissionTooltips: boolean;
    showRoleBadges: boolean;
    hideRestrictedElements: boolean;
    showAccessDeniedReasons: boolean;
  };
}

/**
 * Default RBAC Configuration
 */
export const DEFAULT_RBAC_CONFIG: RBACConfig = {
  version: '1.0.0',
  environment: 'development',
  policies: {
    enabled: true,
    strictMode: false,
    defaultDeny: false,
    auditMode: true,
    customPolicies: []
  },
  features: {
    timeBasedAccess: false,
    ipRestrictions: false,
    sessionTimeout: true,
    multiFactorAuth: false
  },
  audit: {
    logAccess: true,
    logDenials: true,
    logPolicyChanges: true,
    retentionDays: 90
  },
  ui: {
    showPermissionTooltips: true,
    showRoleBadges: true,
    hideRestrictedElements: true,
    showAccessDeniedReasons: true
  }
};

/**
 * Environment-specific configurations
 */
export const ENVIRONMENT_CONFIGS: Record<string, Partial<RBACConfig>> = {
  development: {
    policies: {
      enabled: true,
      strictMode: false,
      auditMode: true,
      defaultDeny: false
    },
    features: {
      timeBasedAccess: false,
      ipRestrictions: false
    },
    ui: {
      showPermissionTooltips: true,
      showAccessDeniedReasons: true
    }
  },
  
  staging: {
    policies: {
      enabled: true,
      strictMode: true,
      auditMode: true,
      defaultDeny: false
    },
    features: {
      timeBasedAccess: true,
      ipRestrictions: false
    },
    audit: {
      retentionDays: 30
    }
  },
  
  production: {
    policies: {
      enabled: true,
      strictMode: true,
      auditMode: false,
      defaultDeny: true
    },
    features: {
      timeBasedAccess: true,
      ipRestrictions: true,
      multiFactorAuth: true
    },
    audit: {
      retentionDays: 365
    },
    ui: {
      showPermissionTooltips: false,
      showAccessDeniedReasons: false
    }
  }
};

/**
 * RBAC Configuration Manager
 */
export class RBACConfigManager {
  private static config: RBACConfig = DEFAULT_RBAC_CONFIG;
  private static initialized = false;

  /**
   * Initialize RBAC configuration
   */
  static initialize(environment?: string): void {
    if (this.initialized) {
      console.warn('RBAC Config already initialized');
      return;
    }

    // Determine environment
    const env = environment || process.env.NODE_ENV || 'development';
    
    // Merge default config with environment-specific config
    const envConfig = ENVIRONMENT_CONFIGS[env] || {};
    this.config = this.mergeConfigs(DEFAULT_RBAC_CONFIG, envConfig);
    this.config.environment = env as any;

    // Load policies if enabled
    if (this.config.policies.enabled) {
      this.loadPolicies();
    }

    this.initialized = true;
    console.log(`RBAC Configuration initialized for ${env} environment`);
  }

  /**
   * Get current configuration
   */
  static getConfig(): RBACConfig {
    if (!this.initialized) {
      this.initialize();
    }
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  static updateConfig(updates: Partial<RBACConfig>): void {
    this.config = this.mergeConfigs(this.config, updates);
    
    // Reload policies if policy settings changed
    if (updates.policies) {
      this.loadPolicies();
    }
  }

  /**
   * Load policies based on configuration
   */
  private static loadPolicies(): void {
    // Clear existing policies
    AdvancedPolicyEngine.clearPolicies();

    // Load default policies (time-based ones only when the feature is on)
    PolicyConfigManager.loadDefaultPolicies({ timeBased: this.config.features.timeBasedAccess });

    // Add custom security policies for proper permission enforcement
    this.addSecurityPolicies();

    // Load custom policies
    if (this.config.policies.customPolicies.length > 0) {
      AdvancedPolicyEngine.registerPolicies(this.config.policies.customPolicies);
    }

    console.log(`Loaded ${AdvancedPolicyEngine.getPolicies().length} RBAC policies`);
  }

  /**
   * Add security policies for proper permission enforcement
   */
  private static addSecurityPolicies(): void {
    // Deny project edit access for team members who don't have explicit edit permissions
    const denyTeamMemberProjectEdit = {
      id: 'deny-team-member-project-edit',
      name: 'Deny Team Member Project Edit',
      description: 'Team members cannot edit projects unless they have explicit edit permissions',
      conditions: [
        {
          type: 'role' as const,
          operator: 'equals' as const,
          field: 'role',
          value: 'team_member'
        },
        {
          type: 'context' as const,
          operator: 'equals' as const,
          field: 'resource',
          value: 'projects'
        },
        {
          type: 'context' as const,
          operator: 'equals' as const,
          field: 'action',
          value: 'edit'
        }
      ],
      effect: 'deny' as const,
      priority: 800
    };

    AdvancedPolicyEngine.registerPolicy(denyTeamMemberProjectEdit);
  }

  /**
   * Add custom policy
   */
  static addCustomPolicy(policy: PolicyRule): void {
    const validation = AdvancedPolicyEngine.validatePolicy(policy);
    if (!validation.valid) {
      throw new Error(`Invalid policy: ${validation.errors.join(', ')}`);
    }

    this.config.policies.customPolicies.push(policy);
    AdvancedPolicyEngine.registerPolicy(policy);

    if (this.config.audit.logPolicyChanges) {
      console.log(`Added custom policy: ${policy.id}`);
    }
  }

  /**
   * Remove custom policy
   */
  static removeCustomPolicy(policyId: string): void {
    this.config.policies.customPolicies = this.config.policies.customPolicies.filter(
      p => p.id !== policyId
    );
    AdvancedPolicyEngine.removePolicy(policyId);

    if (this.config.audit.logPolicyChanges) {
      console.log(`Removed custom policy: ${policyId}`);
    }
  }

  /**
   * Export configuration
   */
  static exportConfig(): string {
    return JSON.stringify(this.config, null, 2);
  }

  /**
   * Import configuration
   */
  static importConfig(configJson: string): void {
    try {
      const importedConfig = JSON.parse(configJson);
      this.updateConfig(importedConfig);
    } catch (error) {
      throw new Error('Invalid configuration JSON');
    }
  }

  /**
   * Reset to default configuration
   */
  static reset(): void {
    this.config = { ...DEFAULT_RBAC_CONFIG };
    this.loadPolicies();
  }

  /**
   * Check if feature is enabled
   */
  static isFeatureEnabled(feature: keyof RBACConfig['features']): boolean {
    return this.getConfig().features[feature];
  }

  /**
   * Check if audit is enabled for specific type
   */
  static isAuditEnabled(type: keyof RBACConfig['audit']): boolean {
    const config = this.getConfig();
    return config.audit[type] as boolean;
  }

  /**
   * Get UI setting
   */
  static getUISetting(setting: keyof RBACConfig['ui']): boolean {
    return this.getConfig().ui[setting];
  }

  /**
   * Merge configuration objects
   */
  private static mergeConfigs(base: RBACConfig, override: Partial<RBACConfig>): RBACConfig {
    const result = { ...base };

    Object.keys(override).forEach(key => {
      const value = override[key as keyof RBACConfig];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result[key as keyof RBACConfig] = {
          ...result[key as keyof RBACConfig],
          ...value
        } as any;
      } else if (value !== undefined) {
        result[key as keyof RBACConfig] = value as any;
      }
    });

    return result;
  }

  /**
   * Validate configuration
   */
  static validateConfig(config: Partial<RBACConfig>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate version
    if (config.version && typeof config.version !== 'string') {
      errors.push('Version must be a string');
    }

    // Validate environment
    if (config.environment && !['development', 'staging', 'production'].includes(config.environment)) {
      errors.push('Environment must be development, staging, or production');
    }

    // Validate custom policies
    if (config.policies?.customPolicies) {
      config.policies.customPolicies.forEach((policy, index) => {
        const validation = AdvancedPolicyEngine.validatePolicy(policy);
        if (!validation.valid) {
          errors.push(`Custom policy ${index}: ${validation.errors.join(', ')}`);
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

// Auto-initialize on import
RBACConfigManager.initialize();

export default RBACConfigManager;
